import { useEffect, useMemo, useState } from 'react';
import { subscribeCandles } from '../services/candleFeed';
import {
  Candle,
  computeHeartSeries,
  heartIntervalFromConfig,
  heartParamsFromConfig,
  requiredCandles
} from '../services/vidyaHeart';

export interface HeartReading {
  /** True while the heart is green and below price, false while it is red and above. */
  isTrendUp: boolean | null;
  level: number | null;
  close: number | null;
  /** Open time of the most recent closed bar. Changes exactly once per bar. */
  barTime: number;
  /** False while the warm-up history is still short. */
  ready: boolean;
  interval: string;
}

const EMPTY: HeartReading = {
  isTrendUp: null,
  level: null,
  close: null,
  barTime: 0,
  ready: false,
  interval: '1m'
};

/**
 * The live HUGE♥HUG heart for a symbol, for anything that wants to display it.
 *
 * Deliberately independent of `aiTradingService`: the heart is worth showing whether or not
 * AI trading happens to be running, and a display should not be able to disturb the engine.
 * Both derive their settings through `heartParamsFromConfig`, so they always describe the
 * same level, and `liveChartStream` reference counts its sockets, so watching the same
 * symbol and interval twice costs one connection.
 */
export function useVidyaHeart(symbol: string | undefined, strategyConfig?: any): HeartReading {
  const interval = useMemo(() => heartIntervalFromConfig(strategyConfig), [strategyConfig]);

  const { vidyaLength, vidyaMomentum, bandDistance } = useMemo(
    () => heartParamsFromConfig(strategyConfig),
    [strategyConfig]
  );

  const [reading, setReading] = useState<HeartReading>(EMPTY);

  useEffect(() => {
    if (!symbol) {
      setReading(EMPTY);
      return;
    }

    const params = { vidyaLength, vidyaMomentum, bandDistance };
    const needed = requiredCandles(params);
    const windowSize = Math.min(Math.max(needed + 200, 500), 1000);
    let cancelled = false;

    const publish = (candles: Candle[]) => {
      if (cancelled) return;

      const latest = candles[candles.length - 1];
      if (!latest) return;

      if (candles.length < needed) {
        setReading({
          isTrendUp: null,
          level: null,
          close: latest.close,
          barTime: latest.time,
          ready: false,
          interval
        });
        return;
      }

      const state = computeHeartSeries(candles, params)[candles.length - 1];
      setReading({
        isTrendUp: state.isTrendUp,
        level: state.level,
        close: latest.close,
        barTime: latest.time,
        ready: true,
        interval
      });
    };

    setReading({ ...EMPTY, interval });

    const unsubscribe = subscribeCandles(symbol, interval, windowSize, {
      onReady: publish,
      onClosedBar: publish,
      onError: () => {
        if (!cancelled) setReading({ ...EMPTY, interval });
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [symbol, interval, vidyaLength, vidyaMomentum, bandDistance]);

  return reading;
}

export default useVidyaHeart;
